import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout, Button, SectionLabel, SectionHeading, PageHero } from "../components.jsx";
import { navy, blue, RESOURCES } from "../data";

const pdfMap = {
  "AI Readiness Assessment": { path: "/downloads/ai-readiness-assessment.pdf", filename: "ai-readiness-assessment.pdf" },
  "AI Automation Guide": { path: "/downloads/ai-automation-guide.pdf", filename: "ai-automation-guide.pdf" },
  "Industry Use Cases": { path: "/downloads/industry-use-cases.pdf", filename: "industry-use-cases.pdf" },
  "Consulting Insights": { path: "/downloads/consulting-insights.pdf", filename: "consulting-insights.pdf" },
};

export default function Resources() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const openModal = (title) => {
    setSelectedResource({ title, path: pdfMap[title].path, filename: pdfMap[title].filename });
    setFormData({ name: "", email: "", phone: "" });
    setError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedResource(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const res = await fetch("https://formspree.io/f/xeevjjop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          resource: selectedResource.title,
        }),
      });

      if (!res.ok) throw new Error("Submission failed");

      closeModal();
      const link = document.createElement('a');
      link.href = selectedResource.path;
      link.download = selectedResource.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <PageHero
        label="Free Resources"
        title="Practical AI Resources for Business Owners"
        subtitle="Download free guides, assessments, and insights to understand how AI can work in your business — without the jargon."
      />

      {/* ── RESOURCE CARDS ──────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {RESOURCES.map(r => (
              <div
                key={r.title}
                className="bg-white rounded-lg p-7 border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
                onClick={() => openModal(r.title)}
              >
                <div className="text-3xl mb-4">{r.icon}</div>
                <h3 className="font-bold text-base mb-2 group-hover:text-blue-600 transition-colors" style={{ color: navy }}>{r.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{r.desc}</p>
                <span className="text-blue-600 text-xs font-bold uppercase tracking-widest">Download Free →</span>
              </div>
            ))}
          </div>

          {/* Newsletter signup */}
          <div className="rounded-lg p-10 text-center border border-blue-100 max-w-2xl mx-auto" style={{ background: "#f0f7ff" }}>
            <SectionLabel>Stay Informed</SectionLabel>
            <h3 className="text-2xl font-bold mb-3" style={{ color: navy }}>Get AI Insights by Email</h3>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">Practical articles on AI strategy, automation implementation, and results from real businesses — delivered twice a month.</p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 border border-gray-200 rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
              />
              <Button>Subscribe</Button>
            </div>
            <p className="text-gray-400 text-xs mt-3">No spam. Unsubscribe any time.</p>
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionLabel>Ready to Take Action?</SectionLabel>
          <SectionHeading>From Resource to Reality</SectionHeading>
          <p className="text-gray-500 mt-4 mb-10 text-base max-w-xl mx-auto leading-relaxed">
            Resources are a great starting point — but the real value comes from understanding how AI applies specifically to your business. Book a free strategy call and we'll show you exactly where to start.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate("/contact")}>Book a Free Strategy Call</Button>
            <Button variant="outline" onClick={() => navigate("/ai-assessment")}>Start With an Assessment</Button>
          </div>
        </div>
      </section>

      {/* ── LEAD CAPTURE MODAL ──────────────────────────────────────────────── */}
      {modalOpen && selectedResource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={closeModal}>
          <div className="absolute inset-0 bg-black/60" />
          <div
            className="relative bg-white rounded-lg shadow-2xl w-full max-w-md p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none"
            >
              &times;
            </button>

            <h3 className="text-xl font-bold mb-1" style={{ color: navy }}>Get Your Free Resource</h3>
            <p className="text-gray-500 text-sm mb-6">Enter your details and your download will start instantly.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                required
                placeholder="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-200 rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
              />
              <input
                type="email"
                required
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border border-gray-200 rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
              />
              <input
                type="tel"
                required
                placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full border border-gray-200 rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors"
              />

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full text-white font-semibold rounded-sm px-7 py-3.5 text-sm uppercase tracking-wide transition-all duration-200 disabled:opacity-60"
                style={{ background: submitting ? "#93c5fd" : blue }}
              >
                {submitting ? "Submitting..." : "Download Now →"}
              </button>
            </form>
          </div>
        </div>
      )}

    </Layout>
  );
}
